import { useEffect } from 'react';
import { useData } from '@/context/DataContext';

export default function PraxisMuensterPage() {
  const { fetchContent, getContentBySection, loading } = useData();

  useEffect(() => {
    fetchContent('services');
  }, [fetchContent]);

  const methodsContent = getContentBySection('services', 'methods');
  const ablaufContent = getContentBySection('services', 'ablauf');
  const costsGkvContent = getContentBySection('services', 'costs_gkv');
  const costsPkvContent = getContentBySection('services', 'costs_pkv');
  const costsSelfContent = getContentBySection('services', 'costs_self');

  const ablaufItems = (ablaufContent?.content || '').split('|').filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="praxis-muenster-page">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-normal text-[#2c3e50] mb-2">Therapieangebote</h1>
        <hr className="mb-8 border-gray-300" />

        <div className="grid md:grid-cols-2 gap-12 mb-12">
          <div>
            <p className="text-[#2c3e50] leading-relaxed mb-4">
              In meiner psychotherapeutischen Praxis in Münster biete ich verhaltenstherapeutisch fundierte Behandlung an. 
              Die Therapie kann privat oder über das Kostenerstattungsverfahren Ihrer Krankenkasse abgerechnet werden.
            </p>
            
            <p className="text-[#2c3e50] leading-relaxed mb-6">
              Sie können bei mir im <strong>Einzel-</strong> als auch in einer <strong>Kleingruppe
              (max. 5 Personen)</strong>, sowie in einer <strong>Online Video Therapie</strong> behandelt werden.
            </p>
            
            <p className="text-[#2c3e50] mb-4">
              Die folgenden <strong>psychischen Erkrankungen</strong> können in meiner Praxis behandelt werden:
            </p>

            <ul className="space-y-1 text-[#2c3e50] list-disc pl-5">
              <li>Anpassungsstörungen</li>
              <li>Burnout und psychische Überlastungsstörungen (z.B. postpartale Erschöpfung)</li>
              <li>Depressionen</li>
              <li>Essstörungen: Anorexia- und Bulimia nervosa, sowie Binge Eating Disorder</li>
              <li>Psychosomatische Störungen</li>
              <li>Chronische Schmerzstörungen</li>
              <li>Schlafstörungen</li>
              <li>Angststörungen</li>
              <li>Zwangsstörungen</li>
              <li>Persönlichkeitsstörungen, bzw. interaktionelle Störungen</li>
              <li>Posttraumatische Belastungsstörung und andere traumatische Folgestörungen</li>
            </ul>
          </div>
          <div>
            <img 
              src="https://psychotherapie-praxis-langer.de/Bilder/BueroMuenster.jpg"
              alt="Büro Münster"
              className="w-full rounded shadow-md mb-6"
            />
            
            <p className="text-[#2c3e50] mb-4">
              Psychotherapeutische Leistungen, die im Indikationskatalog nicht erfasst sind und die damit keine 
              Krankenbehandlung darstellen, können als <strong>Privatleistung</strong> behandelt werden:
            </p>

            <ul className="space-y-1 text-[#2c3e50] list-disc pl-5 mb-6">
              <li>Selbstbehauptungstraining und Stressbewältigungstraining</li>
              <li>Entspannungsverfahren als Präventionsleistung</li>
              <li>Verhaltenstherapie bei Flugangst</li>
              <li>Paargespräche</li>
              <li>Positive Schwangerschaftsbegleitung und Geburtsvorbereitung</li>
              <li>Berufliches Coaching</li>
              <li>Hypnotherapeutische Sitzungen</li>
            </ul>

            <p className="text-[#2c3e50] leading-relaxed">
              {methodsContent?.content}
            </p>
          </div>
        </div>

        <hr className="my-8 border-gray-300" />

        {/* Ablauf */}
        <div className="grid md:grid-cols-2 gap-12 mb-12">
          <div>
            <h2 className="text-2xl font-normal text-[#2c3e50] mb-4">Ablauf</h2>
          </div>
          <div className="text-[#2c3e50] space-y-4">
            <p>
              <strong>Terminvereinbarung:</strong><br />
              Sie erreichen mich unter der Telefonnummer: 0157-51336474 oder per E-Mail: 
              <a href="mailto:Psychotherapie-Praxis-langer@gmx.de" className="text-[#3498db]"> Psychotherapie-Praxis-langer@gmx.de</a>.
              Hinterlassen Sie mir gerne eine Nachricht auf der Mobilbox.
            </p>
            <p className="italic text-sm">
              Bitte beachten Sie Termine, die sie nicht wahrnehmen können so früh wie möglich (mind. jedoch 24 Std. vorher) abzusagen 
              oder zu verschieben. Durch Ihre Mitwirkung ermöglichen wir möglichst vielen Patienten die Chance auf einen Termin.
            </p>
            <p>
              <strong>Sprechstunde und probatorische Sitzungen:</strong><br />
              In der psychotherapeutischen Sprechstunde erfolgt eine erste diagnostische Einschätzung und es werden 
              Behandlungsempfehlungen mit Ihnen erörtert.
            </p>
          </div>
        </div>

        <hr className="my-8 border-gray-300" />

        {/* Costs */}
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-normal text-[#2c3e50] mb-4">Kostenübernahme</h2>
          </div>
          <div className="text-[#2c3e50] space-y-6">
            <div>
              <p className="font-semibold mb-2">Gesetzliche Krankenversicherung:</p>
              <p className="leading-relaxed text-sm">
                Wenn Sie nachweisen können, dass Sie keinen Psychotherapieplatz in einer Praxis mit Kassenzulassung 
                bekommen können und eine dringende Behandlungsnotwendigkeit besteht, ist es möglich bei ihrer 
                gesetzlichen Krankenkasse eine Kostenerstattung zu beantragen.
              </p>
            </div>
            <div>
              <p className="font-semibold mb-2">Private Krankenkassen, Beihilfe:</p>
              <p className="leading-relaxed text-sm">{costsPkvContent?.content}</p>
            </div>
            <div>
              <p className="font-semibold mb-2">Selbstzahler:</p>
              <p className="leading-relaxed text-sm">{costsSelfContent?.content}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
